import { useState } from "react";
import { Eye, EyeOff, Info } from "lucide-react";
import AuthInput from "../AuthInput";

const PasswordStep = ({
  title = "Enter Password",
  description,
  submitLabel = "Verify",
  inlineSubmit = false,
  email,
  onSubmit,
  onForgot,
  setError,
  onVerifyEmail,
  showVerifyEmail = false,
  onBack,
  loading,
  error,
  step,
  isForcePasswordChange,
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false); // Controls button state
  const [showAdminPopup, setShowAdminPopup] = useState(false);
  const shouldInlineSubmit = inlineSubmit || isForcePasswordChange;

  // Handle OTP verification with async safety
  const handleVerifyOtp = async () => {
    if (!onVerifyEmail) return;

    try {
      setVerifyingOtp(true); // Switch button to loading state
      await onVerifyEmail(); // Execute parent callback
    } finally {
      setVerifyingOtp(false); // Reset state after completion
    }
  };
  return (
    <div className="relative">
      {/* Persistent Top Banner */}
      {showVerifyEmail && onVerifyEmail && (
        <div className="fixed top-0 left-0 right-0 sm:top-4 sm:left-1/2 sm:-translate-x-1/2 bg-yellow-50 border border-yellow-300 text-primary sm:rounded-md px-3 sm:px-4 py-2 sm:py-3 z-50 flex flex-col sm:flex-row items-center sm:justify-between gap-2 w-full sm:w-[90%] sm:max-w-lg">
          <span className="text-sm font-medium text-center sm:text-left">
            Please verify your email to receive trip updates.
          </span>
          <button
            onClick={handleVerifyOtp}
            disabled={verifyingOtp}
            className="text-white bg-buttonColor text-xs px-3 py-2 rounded-md sm:ml-4 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {verifyingOtp ? "Please Wait" : "Verify Email"}
          </button>
        </div>
      )}

      {title !== "Login With Password" && !isForcePasswordChange ? (
        <h2 className="text-base font-semibold text-center">{title}</h2>
      ) : null}

      {title === "Login With Password" && email ? (
        <p className="text-sm text-primary text-center pt-1">
          You are signing in with{" "}
          <span className="font-semibold text-secondary">{email}</span>.
        </p>
      ) : null}
      {isForcePasswordChange ? (
        <p className="text-sm text-primary text-center pt-2">
          A temporary password for{" "}
          <span className="text-sm font-bold text-primary text-center">
            {email}{" "}
          </span>
          has been generated and sent to your registered mobile number via SMS
          and to your registered email address. Please enter your{" "}
          <span className="text-secondary">temporary password</span> to
          continue.
        </p>
      ) : null}

      <div className="pt-4">
        <AuthInput
          step={step}
          name="password"
          type={showPassword ? "text" : "password"}
          value={password}
          setValue={setPassword}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={
            isForcePasswordChange
              ? "Enter Generated Password"
              : "Enter password"
          }
          setError={setError}
          rightIcon={
            showPassword ? (
              <Eye
                size={16}
                className="cursor-pointer"
                onClick={() => setShowPassword(false)}
              />
            ) : (
              <EyeOff
                size={16}
                className="cursor-pointer"
                onClick={() => setShowPassword(true)}
              />
            )
          }
          error={error}
          buttonText={shouldInlineSubmit ? submitLabel : undefined}
          onButtonClick={
            shouldInlineSubmit ? () => onSubmit(password) : undefined
          }
          loading={shouldInlineSubmit ? loading && !verifyingOtp : false}
        />
      </div>

      <div className="w-full sm:w-[50%] m-auto flex flex-col gap-4">
        {/* Main Action Row */}
        <div className="flex items-center justify-between pt-2">
          {/* Left side: Forgot Password */}
          <div>
            {onForgot && !isForcePasswordChange && (
              <button
                type="button"
                onClick={onForgot}
                className="text-buttonColor text-sm underline"
              >
                Forgot Password?
              </button>
            )}
          </div>

          {/* Right side: Submit */}
          {!shouldInlineSubmit ? (
            <button
              type="button"
              onClick={() => onSubmit(password)}
              disabled={loading && !verifyingOtp}
              className="cursor-pointer outline-none text-xs bg-buttonColor text-white py-3 px-5 rounded-md disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading && !verifyingOtp ? "Please wait..." : submitLabel}
            </button>
          ) : null}
        </div>

        {!isForcePasswordChange && inlineSubmit ? (
          <p className="text-sm text-primary text-center">
            Enter your password to continue. If you forgot it, tap Forgot
            Password to reset it using your email.
          </p>
        ) : null}
      </div>

      {isForcePasswordChange ? (
        <div className="w-full text-sm text-primary text-center pt-3 space-y-1">
          {/* <p>Please enter your temporary password to continue.</p> */}
          <p>
            The temporary password has been sent from{" "}
            <span className="font-bold">info@biblicaltouring.com</span> via
            email and SMS.
          </p>
          <p>
            Please check your <span className="text-secondary">Inbox</span> as
            well as your{" "}
            <span className="text-secondary">Spam/Junk folder.</span>
          </p>
          <p>
            After signing in, you will be prompted to{" "}
            <span className="text-secondary">
              create your own new password.
            </span>
          </p>
          <p>
            If you have any questions, please{" "}
            <button
              type="button"
              onClick={() => setShowAdminPopup(true)}
              className="text-buttonColor underline"
            >
              contact administrator.
            </button>
          </p>
        </div>
      ) : null}

      {showAdminPopup ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full text-center">
            <Info
              size={34}
              className="mx-auto text-blue-600"
              aria-hidden="true"
            />
            <h3 className="text-base font-semibold">Contact Administrator</h3>
            <p className="text-sm text-primary pt-2">
              Need help with login? Contact us:
            </p>
            <p className="text-sm text-primary pt-2">
              Call:{" "}
              <a
                href="tel:+18604022247"
                className="text-secondary underline font-semibold"
              >
                +1 860 402 2247
              </a>
            </p>
            <p className="text-sm text-primary pt-1">
              Email:{" "}
              <a
                href="mailto:info@biblicaltouring.com"
                className="text-secondary underline font-semibold"
              >
                info@biblicaltouring.com
              </a>
            </p>
            <button
              type="button"
              onClick={() => setShowAdminPopup(false)}
              className="mt-4 bg-buttonColor text-white text-xs px-4 py-2 rounded-md"
            >
              Ok
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PasswordStep;
